import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useAcceptHorseShare } from '../api/hooks'
import { PageSpinner } from '../components/ui'

/**
 * Landing page for a horse-share invitation link (/horse-share/:token).
 * Acceptance is bound server-side to the invited email address, so a forwarded
 * link cannot give a stranger access — a mismatch surfaces as a plain error.
 */
export function HorseShareAccept() {
  const { token = '' } = useParams()
  const { t } = useI18n()
  const nav = useNavigate()
  const accept = useAcceptHorseShare()
  const [err, setErr] = useState('')
  const [done, setDone] = useState<{ horseId: string; horseName: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    accept
      .mutateAsync(token)
      .then((r) => { if (!cancelled) setDone(r as { horseId: string; horseName: string }) })
      .catch((e) => { if (!cancelled) setErr((e as Error).message) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  if (accept.isPending && !done && !err) return <PageSpinner />

  return (
    <main className="mx-auto max-w-lg px-5 py-20 text-center">
      {done ? (
        <>
          <div className="text-4xl">🐎</div>
          <h1 className="mt-3 text-2xl font-extrabold tracking-tight">
            {t('horse.share.accepted', { horse: done.horseName })}
          </h1>
          <p className="mt-2 text-brand-600 dark:text-brand-300">{t('horse.share.acceptedBody')}</p>
          <button onClick={() => nav(`/app/horse/${done.horseId}`)} className="btn-primary mt-6">
            {t('horse.share.view')}
          </button>
        </>
      ) : (
        <>
          <div className="text-4xl">🔒</div>
          <h1 className="mt-3 text-2xl font-extrabold tracking-tight">{t('horse.share.failed')}</h1>
          <p className="mt-2 text-brand-600 dark:text-brand-300">{err || t('horse.share.failedBody')}</p>
          <Link to="/app" className="btn-primary mt-6">{t('horse.share.backHome')}</Link>
        </>
      )}
    </main>
  )
}
